import React, { useState, useEffect, useCallback, useRef } from "react";
import { getOrFetchToken, getToken } from "../../hooks/useViewerToken";

const BASE_URL_DASH = "https://mis.empowerplus.in";

/* ── FILTER DEFINITIONS ── */
const FILTER_DEFS = [
    { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
    { key: "date", label: "Date", type: "daterange" },
    { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
    { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
    { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
    { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
    { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
    { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
    { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
    { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
    { key: "sector", label: "Sector", type: "multi", apiKey: "sectors" },
];

const FLT_DEFAULT_OPEN = { fy: true, state: true };
const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], project: [], phase: [], state: [], district: [], centre: [], gender: [], sector: [] };

/* ── FILTER SIDEBAR ── */
function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
    return (
        <div className="flt-group">
            <button className="flt-group-hd" onClick={onToggle}>
                <span className="flt-group-lbl">{filter.label}</span>
                <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
            </button>
            {open && (
                <div className="flt-body">
                    {filter.type === "daterange" && (
                        <div className="flt-daterange">
                            <label>Start</label>
                            <input type="date" className="flt-date-input" />
                            <label>End</label>
                            <input type="date" className="flt-date-input" />
                        </div>
                    )}
                    {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
                        <label key={opt} className="flt-check-row">
                            <input
                                type="checkbox"
                                className="flt-cb"
                                checked={selected.includes(opt)}
                                onChange={() => onToggleValue(filter.key, opt)}
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
    const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

    const getOptions = (filter) => {
        if (filter.staticOptions) return filter.staticOptions;
        if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
            return apiOptions[filter.apiKey];
        return [];
    };

    const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
    const totalSelected = Object.values(selected).flat().length;

    return (
        <aside className="flt-sidebar">
            <div className="flt-header">
                <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
                <button className="flt-reset" onClick={onReset}>Reset All</button>
            </div>
            <div className="flt-note">All filter views change according to selection.</div>
            {FILTER_DEFS.map(filter => (
                <FilterGroup
                    key={filter.key}
                    filter={filter}
                    open={!!openFilters[filter.key]}
                    onToggle={() => toggle(filter.key)}
                    options={getOptions(filter)}
                    selected={selected[filter.key] || []}
                    onToggleValue={onToggleValue}
                />
            ))}
            <button className="flt-apply" onClick={onApply}>Apply Filters</button>
            <style>{`
.flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
.flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
.flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
.flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
.flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
.flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
.flt-group { border-bottom:1px solid #f1f5f9; }
.flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
.flt-group-hd:hover { color:#2563eb; }
.flt-group-lbl { flex:1; }
.flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
.flt-chevron.open { transform:rotate(270deg); }
.flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
.flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
.flt-check-row:hover { background:#eff6ff; color:#2563eb; }
.flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
.flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
.flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
.flt-date-input:focus { border-color:#2563eb; }
.flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
.flt-apply:hover { background:#1d4ed8; }
            `}</style>
        </aside>
    );
}

/* ─── STATIC DATA (sections 3–7 remain static until requested) ─── */

const ENGAGEMENT_CARDS = [
    { label: "Mechanics Accessing Helpline", value: "3,240", sub: "Unique callers to support line", color: "#2563eb" },
    { label: "Queries Resolved", value: "8,760", sub: "Total resolutions · 91% SLA met", color: "#059669" },
];

const SKILL_ROW1 = [
    { label: "Pre–Post Test Improvement", value: "+34%", sub: "Avg score gain post-training", color: "#2563eb" },
    { label: "Workers Reporting Skill Improvement", value: "78%", sub: "Self-reported in exit survey", color: "#7c3aed" },
    { label: "Adopting New Technologies / Techniques", value: "62%", sub: "3-month follow-up survey", color: "#0e7490" },
];

const SKILL_ROW2 = [
    { label: "Truck Drivers Adopting Digital Payments", value: "54%", sub: "Using UPI / digital wallets post-training", color: "#f59e0b" },
    { label: "Workers Recommending Training", value: "82%", sub: "Net Promoter indicator", color: "#059669" },
];

const ECONOMIC_CARDS = [
    { label: "% Govt Scheme Linkages", value: "48%", sub: "Workers linked to at least one scheme", color: "#2563eb" },
    { label: "Workers Reporting Income Increase", value: "67%", sub: "6-month post-training follow-up", color: "#059669" },
];

const GOVT_SCHEMES_DATA = [
    { name: "PMKVY", count: 1840 },
    { name: "PM SVANidhi", count: 1320 },
    { name: "e-Shram", count: 1180 },
    { name: "MSME Scheme", count: 940 },
    { name: "Others", count: 640 },
];

const SAFETY_CARDS = [
    { label: "% Following Safety Standards", value: "74%", sub: "Observed compliance at worksites", color: "#2563eb" },
    { label: "% Adopting Preventive Maintenance", value: "68%", sub: "Vehicles / equipment maintained", color: "#7c3aed" },
    { label: "% Reduction in Accidents / Penalties", value: "31%", sub: "Compared to pre-training baseline", color: "#059669" },
];

const PROGRAM_EXP = [
    { label: "Training Satisfaction Score", value: "4.1 / 5", sub: "Avg rating from exit surveys", color: "#2563eb" },
    { label: "Recommendation Score", value: "79%", sub: "Would recommend to peers", color: "#059669" },
];

const AGE_DIST_DEFAULT = [
    { range: "Below 18", count: 0 },
    { range: "18 to 25", count: 0 },
    { range: "25 to 35", count: 0 },
    { range: "35+", count: 0 },
];

/* ─── HELPERS ─── */

function SectionBadge({ n, label }) {
    return (
        <div className="uif-section-hd">
            <span className="uif-section-badge">Section {n}</span>
            <span className="uif-section-title">{label}</span>
        </div>
    );
}

function StatCard({ label, value, sub, color }) {
    return (
        <div className="uif-card uif-stat" style={{ borderTop: `4px solid ${color}` }}>
            <div className="uif-stat-val" style={{ color }}>{value}</div>
            <div className="uif-stat-lbl">{label}</div>
            {sub && <div className="uif-stat-sub">{sub}</div>}
        </div>
    );
}

function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1 = "#1e40af", color2 = "#60a5fa" }) {
    if (!data || data.length === 0) {
        return <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No data available</div>;
    }
    const max = Math.max(...data.map(d => d[valueKey]));
    const safeMax = max > 0 ? max : 1;
    return (
        <div className="uif-vbar-wrap" style={{ height }}>
            {data.map(d => (
                <div key={d[nameKey]} className="uif-vbar-item">
                    <div className="uif-vbar-val">{Number(d[valueKey]).toLocaleString()}</div>
                    <div className="uif-vbar-track">
                        <div
                            className="uif-vbar-fill"
                            style={{
                                height: `${(d[valueKey] / safeMax) * 100}%`,
                                background: `linear-gradient(to top, ${color1}, ${color2})`,
                            }}
                        />
                    </div>
                    <div className="uif-vbar-name">{d[nameKey]}</div>
                </div>
            ))}
        </div>
    );
}

/* ─── PAGE ─── */

function buildFilterBody(selected) {
    return {
        fy: selected.fy || [],
        fundingSource: selected.fundingSource || [],
        schemes: selected.scheme || [],
        projects: selected.project || [],
        phases: selected.phase || [],
        states: selected.state || [],
        districts: selected.district || [],
        centers: selected.centre || [],
        gender: selected.gender || [],
        sectors: selected.sector || [],
        projectType: "Upskilling",
    };
}

export default function UpskillingInformalPage() {
    const [selected, setSelected] = useState(EMPTY_FILTERS);
    const [apiOptions, setApiOptions] = useState(null);

    const [demographics, setDemographics] = useState(null);
    const [ageDistribution, setAgeDistribution] = useState(AGE_DIST_DEFAULT);
    const [demoLoading, setDemoLoading] = useState(false);

    const [participation, setParticipation] = useState(null);
    const [participationLoading, setParticipationLoading] = useState(false);

    const fetchDemographics = (filters) => {
        const token = getToken();
        setDemoLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/upskilling-informal/demographics`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data) {
                    setDemographics(data);
                    setAgeDistribution(
                        (data.ageDistribution || []).map(d => ({ range: d.range, count: Number(d.count) }))
                    );
                }
            })
            .catch(() => { })
            .finally(() => setDemoLoading(false));
    };

    const fetchParticipation = (filters) => {
        const token = getToken();
        setParticipationLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/upskilling-informal/participation`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setParticipation(data); })
            .catch(() => { })
            .finally(() => setParticipationLoading(false));
    };

    const loadFilterOptions = useCallback((currentSelected) => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("viewer_token");
        if (!token) return;
        const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
        fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
            method: "POST",
            headers: h,
            body: JSON.stringify(buildFilterBody(currentSelected)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setApiOptions(data); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        (async () => {
            const token = await getOrFetchToken();
            if (!token) return;

            loadFilterOptions(EMPTY_FILTERS);
            fetchDemographics(EMPTY_FILTERS);
            fetchParticipation(EMPTY_FILTERS);
        })();
    }, [loadFilterOptions]);

    const filterDebounceRef = useRef(null);
    useEffect(() => {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            loadFilterOptions(selected);
        }, 300);
        return () => clearTimeout(filterDebounceRef.current);
    }, [selected, loadFilterOptions]);

    const handleToggleValue = (filterKey, value) => {
        setSelected(prev => {
            const current = prev[filterKey] || [];
            return {
                ...prev,
                [filterKey]: current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value],
            };
        });
    };

    const handleApply = () => {
        fetchDemographics(selected);
        fetchParticipation(selected);
    };

    const handleReset = () => {
        const empty = EMPTY_FILTERS;
        setSelected(empty);
        fetchDemographics(empty);
        fetchParticipation(empty);
    };

    return (
        <div className="uif-wrapper">

            {/* ── MAIN ── */}
            <div className="uif-main">

                {/* Page Header */}
                <div className="uif-page-header">
                    <div>
                        <div className="uif-breadcrumb">Impact Dimensions</div>
                        <h1 className="uif-title">Upskilling of Informal Workers</h1>
                        <p className="uif-subtitle">Participation, skill gains, economic uplift and safety outcomes for informal workforce</p>
                    </div>
                    <div className="uif-badge">Section 1–2 · Live Data</div>
                </div>

                {/* ── SECTION 1: Demographics ── */}
                <SectionBadge n={1} label="Demographics" />
                {demoLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading demographics…</div>
                ) : (
                    <>
                        <div className="uif-cards-2">
                            <StatCard
                                label="Gender Split"
                                value={(() => {
                                    const gs = demographics && demographics.genderSplit;
                                    if (!gs || Object.keys(gs).length === 0) return "No data";
                                    const total = Object.values(gs).reduce((a, b) => a + b, 0);
                                    if (total === 0) return "No data";
                                    return Object.entries(gs)
                                        .map(([g, n]) => `${Math.round(n * 100 / total)}% ${g[0].toUpperCase()}`)
                                        .join(" / ");
                                })()}
                                sub="Male-female ratio across Upskilling projects"
                                color="#2563eb"
                            />
                            <StatCard
                                label="Annual Income (Avg)"
                                value={demographics && demographics.avgHouseholdIncome
                                    ? `₹${Number(demographics.avgHouseholdIncome).toLocaleString("en-IN")} / yr`
                                    : "No data"}
                                sub="Annual household income of beneficiaries"
                                color="#0e7490"
                            />
                        </div>
                        <div className="uif-card uif-chart-card" style={{ marginTop: 12 }}>
                            <div className="uif-chart-title">Age Distribution of Beneficiaries</div>
                            <VerticalBarChart
                                data={ageDistribution}
                                nameKey="range"
                                height={160}
                                color1="#92400e"
                                color2="#fbbf24"
                            />
                        </div>
                    </>
                )}

                {/* ── SECTION 2: Participation ── */}
                <SectionBadge n={2} label="Participation" />
                {participationLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading participation data…</div>
                ) : (
                    <>
                        <div className="uif-cards-2" style={{ marginBottom: 12 }}>
                            <StatCard
                                label="Informal Workers Trained"
                                value={participation ? Number(participation.informalWorkersTrained).toLocaleString("en-IN") : "—"}
                                sub="Training completions in Upskilling projects"
                                color="#2563eb"
                            />
                            <StatCard
                                label="Informal Workers Certified"
                                value={participation ? Number(participation.informalWorkersCertified).toLocaleString("en-IN") : "—"}
                                // sub="Certified = true in certificates (Upskilling)"
                                color="#059669"
                            />
                        </div>

                        <div className="uif-card uif-chart-card" style={{ marginBottom: 12 }}>
                            <div className="uif-chart-title">Trade-wise Informal Workers Trained (Top 5)</div>
                            {participation && (participation.topTrades || []).length > 0 ? (
                                <VerticalBarChart
                                    data={participation.topTrades}
                                    height={185}
                                    color1="#1e3a8a"
                                    color2="#60a5fa"
                                />
                            ) : (
                                <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No trade data available</div>
                            )}
                        </div>

                        <div className="uif-card uif-chart-card">
                            <div className="uif-chart-title">Informal Workers Trained by Sector (Top 5)</div>
                            {participation && (participation.topSectors || []).length > 0 ? (
                                <VerticalBarChart
                                    data={participation.topSectors}
                                    height={175}
                                    color1="#4c1d95"
                                    color2="#c4b5fd"
                                />
                            ) : (
                                <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No sector data available</div>
                            )}
                        </div>
                    </>
                )}

                {/* ── SECTION 3: Engagement – Mechanics ── */}
                <SectionBadge n={3} label="Engagement – Mechanics" />
                <div className="uif-cards-2">
                    {ENGAGEMENT_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 4: Skill Outcomes ── */}
                <SectionBadge n={4} label="Skill Outcomes" />
                <div className="uif-cards-3" style={{ marginBottom: 12 }}>
                    {SKILL_ROW1.map(d => <StatCard key={d.label} {...d} />)}
                </div>
                <div className="uif-cards-2">
                    {SKILL_ROW2.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 5: Economic Outcomes ── */}
                <SectionBadge n={5} label="Economic Outcomes" />
                <div className="uif-cards-2" style={{ marginBottom: 12 }}>
                    {ECONOMIC_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                <div className="uif-card uif-chart-card">
                    <div className="uif-chart-title">Government Scheme Linkages — Beneficiary Count by Scheme</div>
                    <VerticalBarChart
                        data={GOVT_SCHEMES_DATA}
                        height={170}
                        color1="#065f46"
                        color2="#34d399"
                    />
                </div>

                {/* ── SECTION 6: Safety Outcomes – Truck Drivers ── */}
                <SectionBadge n={6} label="Safety Outcomes – Truck Drivers" />
                <div className="uif-cards-3">
                    {SAFETY_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 7: Program Experience ── */}
                <SectionBadge n={7} label="Program Experience" />
                <div className="uif-cards-2">
                    {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
                </div>

            </div>

            {/* ── FILTER SIDEBAR ── */}
            <FilterSidebar
                selected={selected}
                onToggleValue={handleToggleValue}
                onApply={handleApply}
                onReset={handleReset}
                apiOptions={apiOptions}
            />

            <style>{`
.uif-wrapper {
    display: flex;
    align-items: flex-start;
    background: #f8fafc;
    min-height: 100vh;
    font-family: Inter, system-ui, sans-serif;
    color: #111827;
}
.uif-main { flex: 1; min-width: 0; padding: 24px 28px; }

/* Page header */
.uif-page-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px;
}
.uif-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
.uif-title      { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
.uif-subtitle   { font-size: 13px; color: #64748b; }
.uif-badge {
    background: #dcfce7; color: #166534;
    font-size: 11px; font-weight: 800; padding: 5px 12px;
    border-radius: 999px; white-space: nowrap;
}

/* Section header */
.uif-section-hd {
    display: flex; align-items: center; gap: 10px; margin: 22px 0 10px;
}
.uif-section-badge {
    background: #92400e; color: #fff;
    font-size: 10px; font-weight: 900; padding: 3px 9px;
    border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
}
.uif-section-title { font-size: 14px; font-weight: 900; color: #0f172a; }

/* Card grids */
.uif-cards-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.uif-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }

/* Stat card */
.uif-card {
    background: #fff; border: 1px solid #e5e7eb;
    border-radius: 12px; padding: 16px 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.uif-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.uif-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
.uif-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

/* Chart card */
.uif-chart-card  { padding: 18px 20px; }
.uif-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 14px; }

/* Vertical bar chart */
.uif-vbar-wrap {
    display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
}
.uif-vbar-item {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; gap: 4px; height: 100%;
}
.uif-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
.uif-vbar-track {
    flex: 1; width: 100%; max-width: 60px;
    background: #f1f5f9; border-radius: 6px 6px 0 0;
    display: flex; align-items: flex-end; overflow: hidden;
}
.uif-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
.uif-vbar-name {
    font-size: 10px; font-weight: 700; color: #6b7280;
    text-align: center; line-height: 1.3; max-width: 72px;
}
            `}</style>
        </div>
    );
}



// import React, { useState, useEffect } from "react";

// const BASE_URL_DASH = "https://mis.empowerplus.in";

// /* ── FILTER DEFINITIONS ── */
// const FILTER_DEFS = [
//     { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
//     { key: "date", label: "Date", type: "daterange" },
//     { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
//     { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
//     { key: "subScheme", label: "Sub Scheme", type: "hierarchical", apiKey: "subSchemes" },
//     { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
//     { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
//     { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
//     { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
//     { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
//     { key: "funder", label: "Funder", type: "multi", apiKey: "funders" },
//     { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
// ];

// const FLT_DEFAULT_OPEN = { fy: true, subScheme: true, state: true };
// const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], subScheme: [], project: [], phase: [], state: [], district: [], centre: [], funder: [], gender: [] };

// /* ── FILTER SIDEBAR ── */
// function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
//     return (
//         <div className="flt-group">
//             <button className="flt-group-hd" onClick={onToggle}>
//                 <span className="flt-group-lbl">{filter.label}</span>
//                 <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
//             </button>
//             {open && (
//                 <div className="flt-body">
//                     {filter.type === "daterange" && (
//                         <div className="flt-daterange">
//                             <label>Start</label>
//                             <input type="date" className="flt-date-input" />
//                             <label>End</label>
//                             <input type="date" className="flt-date-input" />
//                         </div>
//                     )}
//                     {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
//                         <label key={opt} className="flt-check-row">
//                             <input
//                                 type="checkbox"
//                                 className="flt-cb"
//                                 checked={selected.includes(opt)}
//                                 onChange={() => onToggleValue(filter.key, opt)}
//                             />
//                             <span>{opt}</span>
//                         </label>
//                     ))}
//                 </div>
//             )}
//         </div>
//     );
// }

// function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
//     const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

//     const getOptions = (filter) => {
//         if (filter.staticOptions) return filter.staticOptions;
//         if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
//             return apiOptions[filter.apiKey];
//         return [];
//     };

//     const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
//     const totalSelected = Object.values(selected).flat().length;

//     return (
//         <aside className="flt-sidebar">
//             <div className="flt-header">
//                 <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
//                 <button className="flt-reset" onClick={onReset}>Reset All</button>
//             </div>
//             <div className="flt-note">All filter views change according to selection.</div>
//             {FILTER_DEFS.map(filter => (
//                 <FilterGroup
//                     key={filter.key}
//                     filter={filter}
//                     open={!!openFilters[filter.key]}
//                     onToggle={() => toggle(filter.key)}
//                     options={getOptions(filter)}
//                     selected={selected[filter.key] || []}
//                     onToggleValue={onToggleValue}
//                 />
//             ))}
//             <button className="flt-apply" onClick={onApply}>Apply Filters</button>
//             <style>{`
// .flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
// .flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
// .flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
// .flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
// .flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
// .flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
// .flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
// .flt-group { border-bottom:1px solid #f1f5f9; }
// .flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
// .flt-group-hd:hover { color:#2563eb; }
// .flt-group-lbl { flex:1; }
// .flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
// .flt-chevron.open { transform:rotate(270deg); }
// .flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
// .flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
// .flt-check-row:hover { background:#eff6ff; color:#2563eb; }
// .flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
// .flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
// .flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
// .flt-date-input:focus { border-color:#2563eb; }
// .flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
// .flt-apply:hover { background:#1d4ed8; }
//             `}</style>
//         </aside>
//     );
// }

// /* ─── STATIC DATA ─── */

// const DEMOGRAPHICS = [
//     { label: "Gender Split",             value: "78% M / 22% F", sub: "Male-female ratio across all batches",        color: "#2563eb" },
//     { label: "Age Category (Majority)",  value: "26–40 yrs",      sub: "62% of trained workers",                     color: "#7c3aed" },
//     { label: "Annual Income (Avg)",      value: "₹1.8 L / yr",   sub: "Annual household income of beneficiaries",    color: "#0e7490" },
// ];

// const PARTICIPATION_CARDS = [
//     { label: "Informal Workers Trained",    value: "12,350", sub: "Completed training programme",   color: "#2563eb" },
//     { label: "Informal Workers Certified",  value: "9,840",  sub: "79.7% certification rate",       color: "#059669" },
// ];

// const TRADE_WISE_DATA = [
//     { name: "Electrician",      count: 2840 },
//     { name: "Plumber",          count: 2310 },
//     { name: "Welder",           count: 1980 },
//     { name: "AC Technician",    count: 1560 },
//     { name: "Carpenter",        count: 1420 },
//     { name: "Painter",          count: 1240 },
//     { name: "Truck Driver",     count: 1000 },
// ];

// const TOPICS_DATA = [
//     { name: "Safety & Compliance",    count: 3240 },
//     { name: "Technical Skills",       count: 2980 },
//     { name: "Digital Skills",         count: 2410 },
//     { name: "Financial Literacy",     count: 1980 },
//     { name: "Entrepreneurship",       count: 1740 },
// ];

// const ENGAGEMENT_CARDS = [
//     { label: "Mechanics Accessing Helpline", value: "3,240", sub: "Unique callers to support line",    color: "#2563eb" },
//     { label: "Queries Resolved",             value: "8,760", sub: "Total resolutions · 91% SLA met",   color: "#059669" },
// ];

// const SKILL_ROW1 = [
//     { label: "Pre–Post Test Improvement",              value: "+34%", sub: "Avg score gain post-training",             color: "#2563eb" },
//     { label: "Workers Reporting Skill Improvement",    value: "78%",  sub: "Self-reported in exit survey",             color: "#7c3aed" },
//     { label: "Adopting New Technologies / Techniques", value: "62%",  sub: "3-month follow-up survey",                 color: "#0e7490" },
// ];

// const SKILL_ROW2 = [
//     { label: "Truck Drivers Adopting Digital Payments", value: "54%", sub: "Using UPI / digital wallets post-training", color: "#f59e0b" },
//     { label: "Workers Recommending Training",            value: "82%", sub: "Net Promoter indicator",                    color: "#059669" },
// ];

// const ECONOMIC_CARDS = [
//     { label: "% Govt Scheme Linkages",                  value: "48%", sub: "Workers linked to at least one scheme",    color: "#2563eb" },
//     { label: "Workers Reporting Income Increase",        value: "67%", sub: "6-month post-training follow-up",          color: "#059669" },
// ];

// const GOVT_SCHEMES_DATA = [
//     { name: "PMKVY",        count: 1840 },
//     { name: "PM SVANidhi",  count: 1320 },
//     { name: "e-Shram",      count: 1180 },
//     { name: "MSME Scheme",  count: 940  },
//     { name: "Others",       count: 640  },
// ];

// const SAFETY_CARDS = [
//     { label: "% Following Safety Standards",       value: "74%", sub: "Observed compliance at worksites",       color: "#2563eb" },
//     { label: "% Adopting Preventive Maintenance",  value: "68%", sub: "Vehicles / equipment maintained",        color: "#7c3aed" },
//     { label: "% Reduction in Accidents / Penalties",value: "31%",sub: "Compared to pre-training baseline",      color: "#059669" },
// ];

// const PROGRAM_EXP = [
//     { label: "Training Satisfaction Score", value: "4.1 / 5", sub: "Avg rating from exit surveys", color: "#2563eb" },
//     { label: "Recommendation Score",         value: "79%",     sub: "Would recommend to peers",     color: "#059669" },
// ];

// /* ─── HELPERS ─── */

// function SectionBadge({ n, label }) {
//     return (
//         <div className="uif-section-hd">
//             <span className="uif-section-badge">Section {n}</span>
//             <span className="uif-section-title">{label}</span>
//         </div>
//     );
// }

// function StatCard({ label, value, sub, color }) {
//     return (
//         <div className="uif-card uif-stat" style={{ borderTop: `4px solid ${color}` }}>
//             <div className="uif-stat-val" style={{ color }}>{value}</div>
//             <div className="uif-stat-lbl">{label}</div>
//             {sub && <div className="uif-stat-sub">{sub}</div>}
//         </div>
//     );
// }

// function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1 = "#1e40af", color2 = "#60a5fa" }) {
//     const max = Math.max(...data.map(d => d[valueKey]));
//     return (
//         <div className="uif-vbar-wrap" style={{ height }}>
//             {data.map(d => (
//                 <div key={d[nameKey]} className="uif-vbar-item">
//                     <div className="uif-vbar-val">{d[valueKey].toLocaleString()}</div>
//                     <div className="uif-vbar-track">
//                         <div
//                             className="uif-vbar-fill"
//                             style={{
//                                 height: `${(d[valueKey] / max) * 100}%`,
//                                 background: `linear-gradient(to top, ${color1}, ${color2})`,
//                             }}
//                         />
//                     </div>
//                     <div className="uif-vbar-name">{d[nameKey]}</div>
//                 </div>
//             ))}
//         </div>
//     );
// }

// /* ─── PAGE ─── */

// export default function UpskillingInformalPage() {
//     const [selected, setSelected] = useState(EMPTY_FILTERS);
//     const [apiOptions, setApiOptions] = useState(null);

//     useEffect(() => {
//         const token = localStorage.getItem("token");
//         fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
//             headers: { Authorization: `Bearer ${token}` },
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setApiOptions(data); })
//             .catch(() => {});
//     }, []);

//     const handleToggleValue = (filterKey, value) => {
//         setSelected(prev => {
//             const current = prev[filterKey] || [];
//             return {
//                 ...prev,
//                 [filterKey]: current.includes(value)
//                     ? current.filter(v => v !== value)
//                     : [...current, value],
//             };
//         });
//     };

//     const handleReset = () => setSelected(EMPTY_FILTERS);

//     return (
//         <div className="uif-wrapper">

//             {/* ── MAIN ── */}
//             <div className="uif-main">

//                 {/* Page Header */}
//                 <div className="uif-page-header">
//                     <div>
//                         <div className="uif-breadcrumb">Impact Dimensions</div>
//                         <h1 className="uif-title">Upskilling of Informal Workers</h1>
//                         <p className="uif-subtitle">Participation, skill gains, economic uplift and safety outcomes for informal workforce</p>
//                     </div>
//                     <div className="uif-badge">Static · FY 2024–25</div>
//                 </div>

//                 {/* ── SECTION 1: Demographics ── */}
//                 <SectionBadge n={1} label="Demographics" />
//                 <div className="uif-cards-3">
//                     {DEMOGRAPHICS.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* ── SECTION 2: Participation ── */}
//                 <SectionBadge n={2} label="Participation" />
//                 <div className="uif-cards-2" style={{ marginBottom: 12 }}>
//                     {PARTICIPATION_CARDS.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* Trade wise bar chart */}
//                 <div className="uif-card uif-chart-card" style={{ marginBottom: 12 }}>
//                     <div className="uif-chart-title">Trade-wise Informal Workers Trained</div>
//                     <VerticalBarChart
//                         data={TRADE_WISE_DATA}
//                         height={185}
//                         color1="#1e3a8a"
//                         color2="#60a5fa"
//                     />
//                 </div>

//                 {/* Topics bar chart */}
//                 <div className="uif-card uif-chart-card">
//                     <div className="uif-chart-title">Informal Workers Trained by Topic</div>
//                     <VerticalBarChart
//                         data={TOPICS_DATA}
//                         height={175}
//                         color1="#4c1d95"
//                         color2="#c4b5fd"
//                     />
//                 </div>

//                 {/* ── SECTION 3: Engagement – Mechanics ── */}
//                 <SectionBadge n={3} label="Engagement – Mechanics" />
//                 <div className="uif-cards-2">
//                     {ENGAGEMENT_CARDS.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* ── SECTION 4: Skill Outcomes ── */}
//                 <SectionBadge n={4} label="Skill Outcomes" />
//                 <div className="uif-cards-3" style={{ marginBottom: 12 }}>
//                     {SKILL_ROW1.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>
//                 <div className="uif-cards-2">
//                     {SKILL_ROW2.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* ── SECTION 5: Economic Outcomes ── */}
//                 <SectionBadge n={5} label="Economic Outcomes" />
//                 <div className="uif-cards-2" style={{ marginBottom: 12 }}>
//                     {ECONOMIC_CARDS.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* Govt Scheme linkages bar chart */}
//                 <div className="uif-card uif-chart-card">
//                     <div className="uif-chart-title">Government Scheme Linkages — Beneficiary Count by Scheme</div>
//                     <VerticalBarChart
//                         data={GOVT_SCHEMES_DATA}
//                         height={170}
//                         color1="#065f46"
//                         color2="#34d399"
//                     />
//                 </div>

//                 {/* ── SECTION 6: Safety Outcomes – Truck Drivers ── */}
//                 <SectionBadge n={6} label="Safety Outcomes – Truck Drivers" />
//                 <div className="uif-cards-3">
//                     {SAFETY_CARDS.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* ── SECTION 7: Program Experience ── */}
//                 <SectionBadge n={7} label="Program Experience" />
//                 <div className="uif-cards-2">
//                     {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//             </div>

//             {/* ── FILTER SIDEBAR ── */}
//             <FilterSidebar
//                 selected={selected}
//                 onToggleValue={handleToggleValue}
//                 onApply={() => {}}
//                 onReset={handleReset}
//                 apiOptions={apiOptions}
//             />

//             <style>{`
// .uif-wrapper {
//     display: flex;
//     align-items: flex-start;
//     background: #f8fafc;
//     min-height: 100vh;
//     font-family: Inter, system-ui, sans-serif;
//     color: #111827;
// }
// .uif-main { flex: 1; min-width: 0; padding: 24px 28px; }

// /* Page header */
// .uif-page-header {
//     display: flex; justify-content: space-between; align-items: flex-start;
//     margin-bottom: 20px;
// }
// .uif-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
// .uif-title      { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
// .uif-subtitle   { font-size: 13px; color: #64748b; }
// .uif-badge {
//     background: #fef3c7; color: #92400e;
//     font-size: 11px; font-weight: 800; padding: 5px 12px;
//     border-radius: 999px; white-space: nowrap;
// }

// /* Section header */
// .uif-section-hd {
//     display: flex; align-items: center; gap: 10px; margin: 22px 0 10px;
// }
// .uif-section-badge {
//     background: #92400e; color: #fff;
//     font-size: 10px; font-weight: 900; padding: 3px 9px;
//     border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
// }
// .uif-section-title { font-size: 14px; font-weight: 900; color: #0f172a; }

// /* Card grids */
// .uif-cards-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
// .uif-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }

// /* Stat card */
// .uif-card {
//     background: #fff; border: 1px solid #e5e7eb;
//     border-radius: 12px; padding: 16px 14px;
//     box-shadow: 0 1px 3px rgba(0,0,0,0.05);
// }
// .uif-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
// .uif-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
// .uif-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

// /* Chart card */
// .uif-chart-card  { padding: 18px 20px; }
// .uif-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 14px; }

// /* Vertical bar chart */
// .uif-vbar-wrap {
//     display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
// }
// .uif-vbar-item {
//     flex: 1; display: flex; flex-direction: column;
//     align-items: center; gap: 4px; height: 100%;
// }
// .uif-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
// .uif-vbar-track {
//     flex: 1; width: 100%; max-width: 60px;
//     background: #f1f5f9; border-radius: 6px 6px 0 0;
//     display: flex; align-items: flex-end; overflow: hidden;
// }
// .uif-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
// .uif-vbar-name {
//     font-size: 10px; font-weight: 700; color: #6b7280;
//     text-align: center; line-height: 1.3; max-width: 72px;
// }
//             `}</style>
//         </div>
//     );
// }
